import React, { useState } from "react";
import { useParams } from "react-router-dom";
import MainCalculatorsList from "../MainCalculators";
import SharedHelmet from "../../Helmet";
import RelatedLinks from "../../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../../Ads";
function VitaminDoseCalculator() {
  const { vitamin } = useParams();
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("male");
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);
  const [dose, setDose] = useState(null);
  const [error, setError] = useState("");
  // Vitamin names mapping
  const vitaminNames = {
    a: "Vitamin A",
    c: "Vitamin C",
    d: "Vitamin D",
    e: "Vitamin E",
    k: "Vitamin K",
    b1: "Tiamin (B1)",
    thiamin: "Tiamin (B1)",
    b2: "Riboflavin (B2)",
    riboflavin: "Riboflavin (B2)",
    b3: "Niasin (B3)",
    niacin: "Niasin (B3)",
    b5: "Asam Pantotenat (B5)",
    pantothenic_acid: "Asam Pantotenat (B5)",
    b6: "Piridoksin (B6)",
    pyridoxine: "Piridoksin (B6)",
    b7: "Biotin (B7)",
    biotin: "Biotin (B7)",
    b9: "Asam Folat (B9)",
    folate: "Asam Folat (B9)",
    b12: "Kobalamin (B12)",
    caynocobalamin: "Kobalamin (B12)",
    choline: "Kolin",
  };
  const vitaminUnits = {
    a: "mikrogram",
    c: "mg",
    d: "mikrogram",
    e: "mg",
    k: "mikrogram",
    b1: "mg",
    thiamin: "mg",
    b2: "mg",
    riboflavin: "mg",
    b3: "mg",
    niacin: "mg",
    b5: "mg",
    pantothenic_acid: "mg",
    b6: "mg",
    pyridoxine: "mg",
    b7: "mikrogram",
    biotin: "mikrogram",
    b9: "mikrogram",
    folate: "mikrogram",
    b12: "mikrogram",
    caynocobalamin: "mikrogram",
    choline: "mg",
  };
  const normalizedVitamin = vitamin.toLowerCase();
  const vitaminName = vitaminNames[normalizedVitamin] || vitamin;
  const vitaminUnit = vitaminUnits[normalizedVitamin] || "mikrogram";
  // Map URL vitamins to internal keys
  const vitaminKeyMap = {
    a: "A",
    c: "C",
    d: "D",
    e: "E",
    k: "K",
    b1: "B1",
    thiamin: "B1",
    b2: "B2",
    riboflavin: "B2",
    b3: "B3",
    niacin: "B3",
    b5: "B5",
    pantothenic_acid: "B5",
    b6: "B6",
    pyridoxine: "B6",
    b7: "B7",
    biotin: "B7",
    b9: "B9",
    folate: "B9",
    b12: "B12",
    caynocobalamin: "B12",
    choline: "Choline",
  };
  const vitaminKey = vitaminKeyMap[normalizedVitamin] || "A";
  // Get vitamin dose based on age, sex, pregnancy, and lactation
  const getVitaminDose = (vitKey, ageValue, gender, pregnant, lactating) => {
    const dosageTable = {
      A: {
        male: {
          "0-6m": 400,
          "7-12m": 500,
          "1-3y": 300,
          "4-8y": 400,
          "9-13y": 600,
          "14-18y": 900,
          "19-50y": 900,
          "51+y": 900,
        },
        female: {
          "0-6m": 400,
          "7-12m": 500,
          "1-3y": 300,
          "4-8y": 400,
          "9-13y": 600,
          "14-18y": 700,
          "19-50y": 700,
          "51+y": 700,
        },
        pregnant: 770,
        lactating: 1300,
      },
      C: {
        male: {
          "0-6m": 40,
          "7-12m": 50,
          "1-3y": 15,
          "4-8y": 25,
          "9-13y": 45,
          "14-18y": 75,
          "19-50y": 90,
          "51+y": 90,
        },
        female: {
          "0-6m": 40,
          "7-12m": 50,
          "1-3y": 15,
          "4-8y": 25,
          "9-13y": 45,
          "14-18y": 65,
          "19-50y": 75,
          "51+y": 75,
        },
        pregnant: 85,
        lactating: 120,
      },
      D: {
        male: {
          "0-6m": 10,
          "7-12m": 10,
          "1-3y": 15,
          "4-8y": 15,
          "9-13y": 15,
          "14-18y": 15,
          "19-50y": 15,
          "51-70y": 15,
          "71+y": 20,
        },
        female: {
          "0-6m": 10,
          "7-12m": 10,
          "1-3y": 15,
          "4-8y": 15,
          "9-13y": 15,
          "14-18y": 15,
          "19-50y": 15,
          "51-70y": 15,
          "71+y": 20,
        },
        pregnant: 15,
        lactating: 15,
      },
      E: {
        male: {
          "0-6m": 4,
          "7-12m": 5,
          "1-3y": 6,
          "4-8y": 7,
          "9-13y": 11,
          "14-18y": 15,
          "19-50y": 15,
          "51+y": 15,
        },
        female: {
          "0-6m": 4,
          "7-12m": 5,
          "1-3y": 6,
          "4-8y": 7,
          "9-13y": 11,
          "14-18y": 15,
          "19-50y": 15,
          "51+y": 15,
        },
        pregnant: 15,
        lactating: 19,
      },
      K: {
        male: {
          "0-6m": 2,
          "7-12m": 2.5,
          "1-3y": 30,
          "4-8y": 55,
          "9-13y": 60,
          "14-18y": 75,
          "19-50y": 120,
          "51+y": 120,
        },
        female: {
          "0-6m": 2,
          "7-12m": 2.5,
          "1-3y": 30,
          "4-8y": 55,
          "9-13y": 60,
          "14-18y": 75,
          "19-50y": 90,
          "51+y": 90,
        },
        pregnant: 90,
        lactating: 90,
      },
      B1: {
        male: {
          "0-6m": 0.2,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.2,
          "19-50y": 1.2,
          "51+y": 1.2,
        },
        female: {
          "0-6m": 0.2,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.0,
          "19-50y": 1.1,
          "51+y": 1.1,
        },
        pregnant: 1.4,
        lactating: 1.4,
      },
      B2: {
        male: {
          "0-6m": 0.3,
          "7-12m": 0.4,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.3,
          "19-50y": 1.3,
          "51+y": 1.3,
        },
        female: {
          "0-6m": 0.3,
          "7-12m": 0.4,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 0.9,
          "14-18y": 1.0,
          "19-50y": 1.1,
          "51+y": 1.1,
        },
        pregnant: 1.4,
        lactating: 1.6,
      },
      B3: {
        male: {
          "0-6m": 2,
          "7-12m": 4,
          "1-3y": 6,
          "4-8y": 8,
          "9-13y": 12,
          "14-18y": 16,
          "19-50y": 16,
          "51+y": 16,
        },
        female: {
          "0-6m": 2,
          "7-12m": 4,
          "1-3y": 6,
          "4-8y": 8,
          "9-13y": 12,
          "14-18y": 14,
          "19-50y": 14,
          "51+y": 14,
        },
        pregnant: 18,
        lactating: 17,
      },
      B6: {
        male: {
          "0-6m": 0.1,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 1.0,
          "14-18y": 1.3,
          "19-50y": 1.3,
          "51-70y": 1.7,
          "71+y": 1.7,
        },
        female: {
          "0-6m": 0.1,
          "7-12m": 0.3,
          "1-3y": 0.5,
          "4-8y": 0.6,
          "9-13y": 1.0,
          "14-18y": 1.2,
          "19-50y": 1.3,
          "51-70y": 1.5,
          "71+y": 1.5,
        },
        pregnant: 1.9,
        lactating: 2.0,
      },
      B9: {
        male: {
          "0-6m": 65,
          "7-12m": 80,
          "1-3y": 150,
          "4-8y": 200,
          "9-13y": 300,
          "14-18y": 400,
          "19-50y": 400,
          "51+y": 400,
        },
        female: {
          "0-6m": 65,
          "7-12m": 80,
          "1-3y": 150,
          "4-8y": 200,
          "9-13y": 300,
          "14-18y": 400,
          "19-50y": 400,
          "51+y": 400,
        },
        pregnant: 600,
        lactating: 500,
      },
      B12: {
        male: {
          "0-6m": 0.4,
          "7-12m": 0.5,
          "1-3y": 0.9,
          "4-8y": 1.2,
          "9-13y": 1.8,
          "14-18y": 2.4,
          "19-50y": 2.4,
          "51+y": 2.4,
        },
        female: {
          "0-6m": 0.4,
          "7-12m": 0.5,
          "1-3y": 0.9,
          "4-8y": 1.2,
          "9-13y": 1.8,
          "14-18y": 2.4,
          "19-50y": 2.4,
          "51+y": 2.4,
        },
        pregnant: 2.6,
        lactating: 2.8,
      },
      B5: {
        male: {
          "0-6m": 1.7,
          "7-12m": 1.8,
          "1-3y": 2,
          "4-8y": 3,
          "9-13y": 4,
          "14-18y": 5,
          "19-50y": 5,
          "51+y": 5,
        },
        female: {
          "0-6m": 1.7,
          "7-12m": 1.8,
          "1-3y": 2,
          "4-8y": 3,
          "9-13y": 4,
          "14-18y": 5,
          "19-50y": 5,
          "51+y": 5,
        },
        pregnant: 6,
        lactating: 7,
      },
      B7: {
        male: {
          "0-6m": 5,
          "7-12m": 6,
          "1-3y": 8,
          "4-8y": 12,
          "9-13y": 20,
          "14-18y": 25,
          "19-50y": 30,
          "51+y": 30,
        },
        female: {
          "0-6m": 5,
          "7-12m": 6,
          "1-3y": 8,
          "4-8y": 12,
          "9-13y": 20,
          "14-18y": 25,
          "19-50y": 30,
          "51+y": 30,
        },
        pregnant: 30,
        lactating: 35,
      },
      Choline: {
        male: {
          "0-6m": 125,
          "7-12m": 150,
          "1-3y": 200,
          "4-8y": 250,
          "9-13y": 375,
          "14-18y": 550,
          "19-50y": 550,
          "51+y": 550,
        },
        female: {
          "0-6m": 125,
          "7-12m": 150,
          "1-3y": 200,
          "4-8y": 250,
          "9-13y": 375,
          "14-18y": 400,
          "19-50y": 425,
          "51+y": 425,
        },
        pregnant: 450,
        lactating: 550,
      },
    };
    // Determine age category
    let ageCategory;
    const ageNum = parseInt(ageValue);
    if (ageNum < 1) {
      if (ageNum < 0.5) ageCategory = "0-6m";
      else ageCategory = "7-12m";
    } else if (ageNum >= 1 && ageNum <= 3) {
      ageCategory = "1-3y";
    } else if (ageNum >= 4 && ageNum <= 8) {
      ageCategory = "4-8y";
    } else if (ageNum >= 9 && ageNum <= 13) {
      ageCategory = "9-13y";
    } else if (ageNum >= 14 && ageNum <= 18) {
      ageCategory = "14-18y";
    } else if (ageNum >= 19 && ageNum <= 50) {
      ageCategory = "19-50y";
    } else if (ageNum >= 51 && ageNum <= 70) {
      ageCategory = "51-70y" in dosageTable[vitKey][gender]
        ? "51-70y"
        : "51+y";
    } else {
      ageCategory = "71+y" in dosageTable[vitKey][gender] ? "71+y" : "51+y";
    }
    // Check for pregnancy or lactation
    if (gender === "female" && pregnant) {
      return dosageTable[vitKey].pregnant;
    }
    if (gender === "female" && lactating) {
      return dosageTable[vitKey].lactating;
    }
    return dosageTable[vitKey][gender][ageCategory];
  };
  const calculateDailyDose = () => {
    try {
      if (!age || age < 0 || age > 120) {
        setError("Harap masukkan usia yang valid (0-120 tahun)");
        setDose(null);
        return;
      }
      const calculatedDose = getVitaminDose(
        vitaminKey,
        age,
        sex,
        isPregnant,
        isLactating
      );
      setDose(calculatedDose);
      setError("");
    } catch (error) {
      setDose(null);
      setError("Terjadi kesalahan dalam menghitung dosis. Harap periksa data yang dimasukkan.");
    }
  };
  const handleCalculate = (e) => {
    e.preventDefault();
    calculateDailyDose();
  };
  const handleReset = () => {
    setAge("");
    setSex("male");
    setIsPregnant(false);
    setIsLactating(false);
    setDose(null);
    setError("");
  };
  const TITLE = `Kalkulator ${vitaminName} - Hitung Kebutuhan Harian Anda akan ${vitaminName}`;
  const DESCRIPTION = `Hitung dosis harian optimal ${vitaminName} berdasarkan usia dan jenis kelamin menggunakan kalkulator akurat kami. Kalkulator ${vitaminName} memberikan dosis yang tepat untuk pria, wanita, anak-anak, ibu hamil, dan menyusui untuk memastikan kesehatan yang baik. Dapatkan perkiraan akurat kebutuhan harian Anda akan ${vitaminName}.`;
  const KEYWORDS = `dosis harian ${vitaminName}, kebutuhan tubuh akan ${vitaminName}, kalkulator ${vitaminName}, ${vitaminName} untuk ibu hamil, ${vitaminName} untuk anak-anak, kekurangan ${vitaminName}, sumber ${vitaminName}`;
  const OG_URL = `https://do-calculate.com/calculator/id/vitamin_daily_dose/${vitamin}/`;
  // Generate Schema.org structured data
  const generateSchemaData = () => {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Halaman Utama",
          item: "https://do-calculate.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Kalkulator",
          item: "https://do-calculate.com/id/all-calculators/",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Kalkulator Vitamin",
          item: "https://do-calculate.com/calculator/id/vitamin_daily_dose/",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: `Kalkulator ${vitaminName}`,
          item: OG_URL,
        },
      ],
    };
    const calculatorSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: `Kalkulator ${vitaminName}`,
      description: DESCRIPTION,
      url: OG_URL,
      applicationCategory: "HealthApplication",
      operatingSystem: "Any",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
    };
    return [breadcrumbSchema, calculatorSchema];
  };
  // Vitamin-specific information
  const vitaminInfo = {
    a: {
      importance:
        "Vitamin A penting untuk penglihatan yang sehat, terutama penglihatan malam. Meningkatkan sistem kekebalan tubuh dan membantu menjaga kesehatan kulit dan selaput lendir. Juga berperan penting dalam pertumbuhan sel dan regenerasi jaringan, serta mendukung kesehatan tulang dan gigi.",
      sources:
        "Wortel, ubi jalar, labu, bayam, kangkung, brokoli, hati, telur, susu, mangga, aprikot, melon",
      deficiency:
        "Mata kering, rabun senja, sistem kekebalan lemah, kulit kering, pertumbuhan terhambat pada anak-anak, peningkatan risiko infeksi",
    },
    c: {
      importance:
        "Vitamin C adalah antioksidan kuat yang membantu memperkuat sistem kekebalan tubuh dan melindungi sel dari kerusakan. Berkontribusi dalam produksi kolagen yang diperlukan untuk kesehatan kulit, pembuluh darah, dan tulang. Juga membantu penyerapan zat besi dari makanan nabati dan mempercepat penyembuhan luka.",
      sources:
        "Jeruk, lemon, jeruk bali, stroberi, kiwi, jambu biji, paprika (warna-warni), brokoli, tomat, bayam",
      deficiency:
        "Gusi berdarah, penyembuhan luka lambat, mudah memar, kulit kering, sistem kekebalan lemah, nyeri sendi, dalam kasus berat: penyakit kudis (scurvy)",
    },
    d: {
      importance:
        "Vitamin D meningkatkan penyerapan kalsium dan fosfor dalam tubuh, membantu membangun dan menjaga kesehatan tulang dan gigi. Berperan penting dalam meningkatkan fungsi sistem kekebalan tubuh dan mengatur pertumbuhan sel. Juga mempengaruhi suasana hati dan kesehatan jantung.",
      sources:
        "Sinar matahari (10-30 menit setiap hari), ikan berlemak (salmon, tuna, sarden), kuning telur, susu yang diperkaya, jus jeruk yang diperkaya, jamur yang terpapar sinar UV",
      deficiency:
        "Nyeri dan kelemahan tulang, osteoporosis, rakitis pada anak-anak, kelemahan otot, kelelahan kronis, depresi, sistem kekebalan lemah",
    },
    e: {
      importance:
        "Vitamin E adalah antioksidan kuat yang melindungi sel dari kerusakan akibat radikal bebas. Membantu menjaga kesehatan kulit, mata, dan rambut. Berperan dalam meningkatkan sistem kekebalan tubuh dan melindungi jantung serta pembuluh darah. Juga membantu melebarkan pembuluh darah dan mencegah pembekuan darah.",
      sources:
        "Kacang-kacangan (almond, hazelnut, kacang tanah), biji-bijian (bunga matahari, labu), minyak nabati (minyak zaitun, minyak bunga matahari), bayam, brokoli, alpukat, kiwi",
      deficiency:
        "Kelemahan otot, masalah penglihatan, sistem kekebalan lemah, mati rasa dan kesemutan di ekstremitas, kesulitan berjalan, dalam kasus berat: kerusakan saraf",
    },
    k: {
      importance:
        "Vitamin K penting untuk pembekuan darah yang tepat dan mencegah pendarahan berlebihan. Berperan penting dalam kesehatan tulang melalui pengaturan kalsium dan peningkatan kepadatan tulang. Juga berkontribusi pada kesehatan jantung dan pembuluh darah dengan mencegah kalsifikasi arteri.",
      sources:
        "Sayuran berdaun hijau (kangkung, bayam, selada, brokoli, kubis Brussel), peterseli, ketumbar, minyak nabati, hati, telur, alpukat",
      deficiency:
        "Mudah berdarah dan sulit dihentikan, memar yang sering, gusi berdarah, mimisan, menstruasi berat, dalam kasus berat: pendarahan internal",
    },
    b1: {
      importance:
        "Tiamin (B1) penting untuk mengubah karbohidrat menjadi energi. Mendukung fungsi saraf dan otot yang tepat. Berperan dalam pertumbuhan, perkembangan, dan fungsi sel. Penting untuk kesehatan jantung, sistem pencernaan, dan saraf.",
      sources:
        "Biji-bijian utuh, roti yang diperkaya, pasta, beras merah, daging babi, hati, kacang-kacangan, kacang-kacangan, biji-bijian, ikan",
      deficiency:
        "Kelelahan dan kelemahan, kehilangan nafsu makan, kebingungan dan kehilangan ingatan, mati rasa di tangan dan kaki, dalam kasus berat: beri-beri (penyakit saraf)",
    },
    b2: {
      importance:
        "Riboflavin (B2) membantu mengubah makanan menjadi energi. Meningkatkan kesehatan kulit, mata, dan saraf. Berperan dalam produksi sel darah merah. Membantu metabolisme vitamin lain seperti B6 dan asam folat.",
      sources:
        "Susu dan produk susu, telur, daging tanpa lemak, hati, sayuran berdaun hijau, biji-bijian utuh yang diperkaya, kacang-kacangan",
      deficiency:
        "Radang mulut dan bibir, radang dan kemerahan lidah, pecah-pecah di sudut mulut, radang kulit, mata sensitif terhadap cahaya, anemia",
    },
    b3: {
      importance:
        "Niasin (B3) berkontribusi dalam mengubah makanan menjadi energi. Mendukung kesehatan kulit, sistem saraf, dan sistem pencernaan. Membantu menurunkan kadar kolesterol jahat dan meningkatkan kolesterol baik. Berperan dalam produksi hormon seks dan stres.",
      sources:
        "Daging merah, ayam, ikan, hati, kacang tanah, kacang-kacangan, biji-bijian utuh yang diperkaya, jamur, kentang",
      deficiency:
        "Kelelahan dan sakit kepala, diare, ruam kulit, kebingungan dan kehilangan ingatan, dalam kasus berat: pellagra (penyakit yang menyebabkan demensia, diare, dan radang kulit)",
    },
    b5: {
      importance:
        "Asam Pantotenat (B5) berkontribusi dalam mengubah makanan menjadi energi. Mendukung kesehatan kulit, rambut, dan mata. Berperan dalam sintesis hormon dan kolesterol. Penting untuk produksi sel darah merah dan neurotransmiter.",
      sources:
        "Daging, hati, ayam, ikan, telur, susu, jamur, alpukat, ubi jalar, brokoli, kacang-kacangan",
      deficiency:
        "Sangat jarang, tetapi dapat menyebabkan: kelelahan, insomnia, depresi, iritabilitas, mati rasa di kaki, sakit perut",
    },
    b6: {
      importance:
        "Piridoksin (B6) berperan dalam mengubah makanan menjadi energi. Mendukung fungsi otak dan sistem saraf. Berkontribusi dalam sintesis hemoglobin yang mengangkut oksigen dalam darah. Membantu mengatur suasana hati dan tidur melalui produksi serotonin dan melatonin.",
      sources:
        "Ayam, ikan, daging sapi, hati, kentang, pisang, buncis, biji-bijian utuh yang diperkaya, bayam, kacang-kacangan",
      deficiency:
        "Anemia, ruam kulit, pecah-pecah di bibir, radang lidah, depresi dan iritabilitas, sistem kekebalan lemah, kebingungan",
    },
    b7: {
      importance:
        "Biotin (B7) membantu mengubah makanan menjadi energi. Mendukung kesehatan kulit, rambut, dan kuku. Berperan dalam sintesis asam lemak dan glukosa. Penting untuk pertumbuhan sel dan produksi asam amino.",
      sources:
        "Kuning telur, hati, kacang-kacangan (terutama almond), biji-bijian, salmon, alpukat, ubi jalar, bayam, brokoli",
      deficiency:
        "Kerontokan rambut, ruam kulit di sekitar mata, hidung, dan mulut, kuku rapuh, kelelahan, depresi, mati rasa di ekstremitas",
    },
    b9: {
      importance:
        "Asam Folat (B9) sangat penting untuk pembentukan sel darah merah dan pertumbuhan sel. Sangat penting terutama untuk ibu hamil untuk mencegah cacat tabung saraf pada janin. Mendukung kesehatan otak dan sistem saraf. Membantu dalam produksi dan perbaikan DNA.",
      sources:
        "Sayuran berdaun hijau (bayam, kangkung), kacang-kacangan (lentil, buncis), buah jeruk, alpukat, brokoli, sereal yang diperkaya, hati",
      deficiency:
        "Anemia makrositik, kelelahan dan kelemahan, sariawan dan lidah, uban dini, pada ibu hamil: risiko cacat janin",
    },
    b12: {
      importance:
        "Kobalamin (B12) penting untuk pembentukan sel darah merah dan kesehatan sistem saraf. Membantu dalam produksi dan perbaikan DNA. Meningkatkan kemampuan tubuh untuk menggunakan lemak dan protein. Berperan dalam pencegahan anemia makrositik.",
      sources:
        "Daging, hati, ikan, telur, susu dan produk susu, sereal yang diperkaya (untuk vegetarian), ragi nutrisi yang diperkaya",
      deficiency:
        "Anemia berat, kelelahan dan kelemahan, kesemutan dan mati rasa di tangan dan kaki, kesulitan keseimbangan dan berjalan, masalah memori dan konsentrasi, radang lidah",
    },
    choline: {
      importance:
        "Kolin penting untuk kesehatan otak, memori, dan fungsi hati. Membantu dalam sintesis neurotransmiter penting untuk memori dan suasana hati. Berperan dalam pertumbuhan sel dan proses metabolisme. Penting untuk transportasi lemak dari hati dan fungsi sistem saraf.",
      sources:
        "Telur (terutama kuning telur), hati, salmon, ayam, daging merah, brokoli, kubis Brussel, kacang tanah, kedelai",
      deficiency:
        "Kerusakan hati, penyakit hati berlemak, kerusakan otot, gangguan memori, masalah perkembangan saraf pada anak-anak",
    },
  };
  const currentVitaminInfo =
    vitaminInfo[normalizedVitamin] || vitaminInfo["a"];
  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={generateSchemaData()}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "12px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                Selamat datang di <strong>Kalkulator {vitaminName}</strong>! Dengan alat akurat ini, Anda dapat menghitung dosis harian optimal {vitaminName} berdasarkan usia, jenis kelamin, dan kondisi kesehatan Anda. Baik Anda pria atau wanita, anak-anak atau dewasa, hamil atau menyusui, kalkulator kami memberikan rekomendasi akurat untuk memastikan Anda mendapatkan jumlah optimal {vitaminName}.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="row">
             <div className="col-sm-12 col-md-6 calculatorStyle">
                <div className="p-3 mb-3 bg-success text-white text-center rounded">
                  <h2 style={{ fontSize: "18px", margin: 0 }}>
                    Hitung Kebutuhan {vitaminName} Anda
                  </h2>
                </div>
                <form onSubmit={handleCalculate}>
                  <div className="form-group mb-3">
                    <label  htmlFor="age">
                      <strong>Usia (tahun):</strong>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="age"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      placeholder="Masukkan usia Anda"
                      min="0"
                      max="120"
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label  htmlFor="sex">
                      <strong>Jenis Kelamin:</strong>
                    </label>
                    <select
                      className="form-control"
                      id="sex"
                      value={sex}
                      onChange={(e) => {
                        setSex(e.target.value);
                        if (e.target.value === "male") {
                          setIsPregnant(false);
                          setIsLactating(false);
                        }
                      }}
                    >
                      <option value="male">Laki-laki</option>
                      <option value="female">Perempuan</option>
                    </select>
                  </div>
                  {sex === "female" && (
                    <>
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="pregnant"
                            checked={isPregnant}
                            onChange={(e) => {
                              setIsPregnant(e.target.checked);
                              if (e.target.checked) setIsLactating(false);
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="pregnant"
                          >
                            Hamil
                          </label>
                        </div>
                      </div>
                      <div className="form-group mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="lactating"
                            checked={isLactating}
                            onChange={(e) => {
                              setIsLactating(e.target.checked);
                              if (e.target.checked) setIsPregnant(false);
                            }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="lactating"
                          >
                            Menyusui
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  <hr />
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn bg-success text-white"
                      style={{ fontSize: "16px", padding: "10px 30px" }}
                    >
                      Hitung
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleReset}
                       style={{ fontSize: "16px", padding: "10px 30px", marginRight: "10px" }}
                    >
                      Hapus

                    </button>
                  </div>
                </form>
              </div>
              <div className="col-sm-12 col-md-4">
                {error && (
                  <div className="alert alert-warning" role="alert">
                    <strong>Kesalahan:</strong> {error}
                  </div>
                )}
                {dose !== null && !error && (
                  <div className="calculatorStyle">
                    <div className="p-3 mb-3 bg-success text-white text-center rounded">
                      <h3 style={{ fontSize: "18px", margin: 0 }}>Hasil</h3>
                    </div>
                    <div
                      className="alert alert-success"
                      style={{ fontSize: "16px", lineHeight: "1.8" }}
                    >
                      <h4 style={{ fontSize: "17px", marginBottom: "15px" }}>
                        Dosis harian yang direkomendasikan:
                      </h4>
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          backgroundColor: "#d4edda",
                          borderRadius: "8px",
                          marginBottom: "15px",
                        }}
                      >
                        <div
                          style={{ fontSize: "18px", marginBottom: "10px" }}
                        >
                          <strong>{vitaminName}</strong>
                        </div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            color: "#155724",
                          }}
                        >
                          {dose} {vitaminUnit}
                        </div>
                      </div>
                      <div style={{ fontSize: "14px", marginTop: "15px" }}>
                        <strong>Catatan:</strong>
                        <ul style={{ marginTop: "10px", lineHeight: "1.6" }}>
                          <li>
                            Dosis ini mewakili Angka Kecukupan Gizi (RDA) yang direkomendasikan
                          </li>
                          <li>
                            Kebutuhan aktual Anda mungkin berbeda tergantung kondisi kesehatan dan pola makan Anda
                          </li>
                          <li>Konsultasikan dengan dokter Anda sebelum mengonsumsi suplemen apa pun</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <hr />
            <div
              className="alert alert-info"
              style={{ fontSize: "15px", lineHeight: "1.8" }}
            >
              Anda juga dapat menggunakan{" "}
              <a href="/calculator/id/vitamin_daily_dose/">
                Kalkulator Dosis Harian Vitamin
              </a>{" "}
              untuk menghitung dosis harian berbagai vitamin.
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      {/* Educational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Pentingnya {vitaminName}</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  {currentVitaminInfo.importance}
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Mengapa Anda Membutuhkan {vitaminName}?
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Mendukung fungsi dasar dan vital tubuh</li>
                  <li>Meningkatkan kesehatan secara umum dan pencegahan penyakit</li>
                  <li>Memastikan pertumbuhan dan perkembangan normal</li>
                  <li>Meningkatkan kualitas hidup dan energi harian</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Sumber Alami {vitaminName}</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Cara terbaik untuk mendapatkan {vitaminName} adalah melalui{" "}
                  <strong>pola makan seimbang</strong>. Berikut sumber alami utama:
                </p>
                <div
                  className="alert alert-success"
                  style={{ lineHeight: "1.8", backgroundColor: "#d4edda" }}
                >
                  <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                    <strong>Sumber Makanan Kaya {vitaminName}:</strong>
                  </h4>
                  <p>{currentVitaminInfo.sources}</p>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Tips Mendapatkan {vitaminName}:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Konsumsi berbagai makanan segar setiap hari</li>
                  <li>Pertahankan pola makan seimbang yang mencakup semua kelompok makanan</li>
                  <li>Hindari memasak berlebihan yang dapat mengurangi nilai vitamin</li>
                  <li>
                    Konsultasikan dengan dokter Anda jika Anda merasa perlu suplemen
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Gejala Kekurangan {vitaminName}</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Kekurangan {vitaminName} dapat menyebabkan berbagai masalah kesehatan. Penting untuk mengenali gejalanya sejak dini:
                </p>
                <div
                  className="alert alert-warning"
                  style={{ lineHeight: "1.8", backgroundColor: "#fff3cd" }}
                >
                  <h4 style={{ fontSize: "16px", marginBottom: "10px" }}>
                    <strong>Tanda dan Gejala Kekurangan:</strong>
                  </h4>
                  <p>{currentVitaminInfo.deficiency}</p>
                </div>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Pencegahan Kekurangan:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Ikuti pola makan seimbang dan kaya vitamin</li>
                  <li>Gunakan kalkulator vitamin untuk mengetahui kebutuhan harian Anda</li>
                  <li>Lakukan pemeriksaan rutin kadar vitamin</li>
                  <li>Konsultasikan dengan dokter jika Anda mengalami gejala kekurangan</li>
                </ul>
                <div
                  className="alert alert-danger mt-3"
                  style={{ lineHeight: "1.8", backgroundColor: "#f8d7da" }}
                >
                  <strong>⚠️ Kapan Harus ke Dokter?</strong>
                  <ul style={{ marginTop: "10px" }}>
                    <li>Jika Anda mengalami gejala kekurangan vitamin yang terus-menerus</li>
                    <li>Sebelum mengonsumsi suplemen dalam dosis tinggi</li>
                    <li>Jika Anda sedang hamil atau berencana hamil</li>
                    <li>Jika Anda menjalani pola makan terbatas</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
         </div>
</div>
  );
}
export default VitaminDoseCalculator;